import React from "react";
import { DotLottleLoader, LoadMoreBtn } from "../Loader";

export interface DataTableColumn<T> {
    key: string;
    header: React.ReactNode;
    render: (row: T) => React.ReactNode;
    width?: number | string;
    className?: string;
}

interface Props<T> {
    columns: DataTableColumn<T>[];
    rows: T[];
    loading: boolean;
    totalCount: number;
    hasMore: boolean;
    error?: string | null;
    onLoadMore: () => void;
    emptyMessage?: string;
    getRowKey?: (row: T, index: number) => React.Key;
    onRowClick?: (row: T) => void;
    // Optional totals row, one cell per column (aligned by index). Rendered
    // as a <tfoot> on desktop and as a summary card on mobile.
    footerCells?: React.ReactNode[];
}

const TH =
    "text-left px-3 py-2.5 text-[10.5px] font-semibold text-text-2 uppercase tracking-[0.5px] border-b border-border-token bg-surface-2";
const TD = "px-3 py-3 border-b border-border-token align-middle";

export function DataTable<T>({
    columns,
    rows,
    loading,
    totalCount,
    hasMore,
    error,
    onLoadMore,
    emptyMessage = "No results found",
    getRowKey,
    onRowClick,
    footerCells,
}: Props<T>) {
    const labeledColumns = columns.filter((c) => c.header);
    const actionColumns = columns.filter((c) => !c.header);

    return (
        <>
            <div className="hidden md:block overflow-x-auto">
                <table className="w-full border-collapse text-[13px] text-text-1">
                    <thead>
                        <tr>
                            {columns.map((c) => (
                                <th key={c.key} className={TH} style={c.width ? { width: c.width } : undefined}>
                                    {c.header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {!loading && rows.length === 0 && (
                            <tr>
                                <td colSpan={columns.length} className="text-center py-8 text-text-2 text-[13px]">
                                    {emptyMessage}
                                </td>
                            </tr>
                        )}
                        {rows.map((row, i) => (
                            <tr
                                key={getRowKey ? getRowKey(row, i) : (row as any).id ?? i}
                                className={`hover:bg-surface-2 ${onRowClick ? "cursor-pointer" : ""}`}
                                onClick={onRowClick ? () => onRowClick(row) : undefined}
                            >
                                {columns.map((c) => (
                                    <td key={c.key} className={`${TD} ${c.className ?? ""}`}>
                                        {c.render(row)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                    {footerCells && rows.length > 0 && (
                        <tfoot>
                            <tr className="bg-surface-2 font-semibold">
                                {columns.map((c, i) => (
                                    <td key={c.key} className={`${TD} ${c.className ?? ""}`}>
                                        {footerCells[i] ?? null}
                                    </td>
                                ))}
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            <div className="md:hidden flex flex-col gap-2 w-full text-text-1">
                {!loading && rows.length === 0 && (
                    <div className="text-center py-8 text-text-2 text-[13px] border border-border-token rounded-lg bg-surface">
                        {emptyMessage}
                    </div>
                )}
                {footerCells && rows.length > 0 && (
                    <div className="w-full border border-border-token rounded-lg p-3 bg-surface-2 font-semibold">
                        <div className="flex flex-col gap-1.5">
                            {labeledColumns.map((c) => {
                                const idx = columns.indexOf(c);
                                const cell = footerCells[idx];
                                if (cell === null || cell === undefined) return null;
                                return (
                                    <div key={c.key} className="flex items-start justify-between gap-3 py-1">
                                        <span className="text-text-2 text-[10.5px] uppercase font-semibold tracking-[0.5px] shrink-0">
                                            {c.header}
                                        </span>
                                        <div className={`text-right text-[12.5px] min-w-0 ${c.className ?? ""}`}>{cell}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                {rows.map((row, i) => (
                    <div
                        key={getRowKey ? getRowKey(row, i) : (row as any).id ?? i}
                        className={`w-full border border-border-token rounded-lg p-3 bg-surface ${onRowClick ? "cursor-pointer" : ""}`}
                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                    >
                        <div className="flex flex-col gap-1.5">
                            {labeledColumns.map((c) => (
                                <div
                                    key={c.key}
                                    className="flex items-start justify-between gap-3 py-1 border-b border-border-token last:border-0"
                                >
                                    <span className="text-text-2 text-[10.5px] uppercase font-semibold tracking-[0.5px] shrink-0">
                                        {c.header}
                                    </span>
                                    <div className={`text-right text-[12.5px] min-w-0 ${c.className ?? ""}`}>
                                        {c.render(row)}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {actionColumns.length > 0 && (
                            <div className="flex items-center justify-end gap-1 mt-2 pt-2 border-t border-border-token">
                                {actionColumns.map((c) => (
                                    <div key={c.key}>{c.render(row)}</div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {loading && rows.length === 0 && (
                <div className="flex justify-center py-8">
                    <DotLottleLoader fill={false} />
                </div>
            )}
            {error && <div className="py-4 text-red-600 text-[13px]">{error}</div>}

            <div className="py-3 pb-6 flex items-center justify-between text-[12px] text-text-2">
                <span>Showing {rows.length} of {totalCount}</span>
            </div>

            {hasMore && <LoadMoreBtn onClick={onLoadMore} loading={loading} />}
        </>
    );
}
